import { Fragment, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Logo, MeshLattice } from './prototype-ui'
import { useSession } from '../hooks/useSession'
import { wsStore, loadWorkspace } from '../lib/workspaceStore'

type NavId =
  | 'features' | 'build' | 'showcase' | 'pricing'
  | 'grids' | 'docs'
  | ''

type NavLink = {
  kind: 'link'
  id: NavId
  label: string
  path: string
}
type NavGroup = {
  kind: 'group'
  id: NavId
  label: string
  items: Array<{ id: string; label: string; path: string; blurb: string }>
}
type NavItem = NavLink | NavGroup

// `Build` collapses MCP/Recipes/Docs/Agents into a dropdown so the bar stays
// short and reads as a learning journey (try → see examples → reference → why).
const PUBLIC_NAV: ReadonlyArray<NavItem> = [
  { kind: 'link',  id: 'features', label: 'Features', path: '/' },
  { kind: 'group', id: 'build',    label: 'Build',
    items: [
      { id: 'mcp',     label: 'MCP setup', path: '/mcp',     blurb: 'Paste one URL into Claude, Cursor, or Windsurf. 30 seconds.' },
      { id: 'recipes', label: 'Recipes',   path: '/recipes', blurb: 'Copy-paste pipelines: Firecrawl, LangGraph, CrewAI, more.' },
      { id: 'docs',    label: 'Docs',      path: '/docs',    blurb: 'Full reference: CLI, API, mesh, versioning, MCP, auth.' },
      { id: 'agents',  label: 'Agents',    path: '/agents',  blurb: 'A four-part manifesto — why this exists, what it\'s for.' },
    ],
  },
  { kind: 'link', id: 'showcase', label: 'Showcase', path: '/showcase' },
  { kind: 'link', id: 'pricing',  label: 'Pricing',  path: '/pricing' },
]

// App nav (logged in): Workspace / billing / api-keys live in the account
// menu so the top-level row stays focused.
const APP_NAV: ReadonlyArray<NavLink> = [
  { kind: 'link', id: 'grids', label: 'Grids', path: '/workspace' },
  { kind: 'link', id: 'docs',  label: 'Docs',  path: '/docs' },
]

function matchPublicActive(pathname: string, items: ReadonlyArray<NavItem>): NavId {
  if (pathname === '/') return 'features'
  for (const item of items) {
    if (item.kind === 'group') {
      if (item.items.some(i => pathname.startsWith(i.path))) return item.id
    } else if (item.path !== '/' && pathname.startsWith(item.path)) {
      return item.id
    }
  }
  return ''
}

interface LayoutProps {
  fullscreen?: boolean
}

export default function Layout({ fullscreen = false }: LayoutProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { status, session, signOut } = useSession()
  const ws = wsStore.use()
  const handle = ws.workspace?.handle ?? null

  // Same shared workspace fetch ProjectList consumes — kicking it off from
  // here means the account menu has the handle ready before the user lands
  // on /workspace.
  useEffect(() => {
    if (status === 'signed-in' && wsStore.get().status === 'idle') loadWorkspace()
  }, [status])

  const signedIn = status === 'signed-in' && !!session
  const navItems: ReadonlyArray<NavItem> = signedIn ? APP_NAV : PUBLIC_NAV
  const activeId: NavId = signedIn
    ? (pathname === '/workspace' || pathname.startsWith('/view/')) ? 'grids'
      : pathname.startsWith('/docs') ? 'docs'
      : ''
    : matchPublicActive(pathname, PUBLIC_NAV)

  async function openBillingPortal() {
    const r = await fetch('/api/billing/portal', { credentials: 'include' })
    if (!r.ok) return
    const { url } = await r.json() as { url: string }
    window.location.href = url
  }

  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Close the drawer on route change so navigation feels normal.
  useEffect(() => { setMobileNavOpen(false) }, [pathname])

  // SPA navigation doesn't reset scroll. Reset on pathname change so /recipes/X
  // (or any nav) lands at the top of the new page. The layout uses
  // `html, body, #root { height: 100% }` so the scroll port can be any of
  // those depending on browser — hit all three. rAF defers to the next paint
  // so the reset happens *after* React swaps the new route's content in.
  useEffect(() => {
    const reset = () => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      const root = document.getElementById('root')
      if (root) root.scrollTop = 0
    }
    reset()
    const id = requestAnimationFrame(reset)
    return () => cancelAnimationFrame(id)
  }, [pathname])

  return (
    <div className="shell scan">
      <MeshLattice enabled={true} density="regular" />

      <nav className="nav">
        <div className="nav-left">
          <Logo onClick={() => navigate(signedIn ? '/workspace' : '/')} />
          <div className="nav-links">
            {navItems.map(l => l.kind === 'link' ? (
              <a key={l.id}
                 className={activeId === l.id ? 'active' : ''}
                 onClick={() => navigate(l.path)}>
                {l.label}
              </a>
            ) : (
              <NavDropdown
                key={l.id}
                label={l.label}
                items={l.items}
                active={activeId === l.id}
                onNavigate={navigate}
              />
            ))}
          </div>
        </div>
        <div className="nav-right">
          {signedIn ? (
            <AccountMenu
              label={handle ?? session.user.email.split('@')[0] ?? 'Account'}
              email={session.user.email}
              onApiKeys={() => navigate('/get-started')}
              onBilling={openBillingPortal}
              onSignOut={async () => { await signOut(); window.location.replace('/') }}
            />
          ) : (
            <>
              <span className="chip mono nav-hide-mobile" style={{ fontSize: 10 }}>
                <span className="dot" style={{ color: '#B8FF3A' }}></span>
                v1.0 · all systems nominal
              </span>
              <button className="btn btn-ghost btn-mono nav-hide-mobile" onClick={() => navigate('/workspace')}>Sign in</button>
              <button className="btn btn-primary nav-hide-mobile" onClick={() => navigate('/get-started')}>
                npm i -g instadash
              </button>
            </>
          )}
          <button
            className="nav-hamburger"
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(v => !v)}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {mobileNavOpen && (
        <MobileNav
          items={navItems}
          activeId={activeId}
          signedIn={signedIn}
          onClose={() => setMobileNavOpen(false)}
          onNavigate={(p) => navigate(p)}
        />
      )}

      <main style={fullscreen ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}>
        <Outlet />
      </main>

      {!fullscreen && <SiteFooter />}
    </div>
  )
}

function NavDropdown({ label, items, active, onNavigate }: {
  label: string
  items: NavGroup['items']
  active: boolean
  onNavigate: (path: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="nav-dropdown">
      <button
        type="button"
        className={`nav-dropdown-trigger${active ? ' active' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(o => !o)}
      >
        {label}<span className="nav-dropdown-chevron">▾</span>
      </button>
      {open && (
        <div className="nav-dropdown-panel" role="menu">
          {items.map(i => (
            <button
              key={i.id}
              type="button"
              role="menuitem"
              className="nav-dropdown-item"
              onClick={() => { onNavigate(i.path); setOpen(false) }}
            >
              <span className="nav-dropdown-item-label">{i.label}</span>
              <span className="nav-dropdown-item-blurb">{i.blurb}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function MobileNav({
  items, activeId, signedIn, onClose, onNavigate,
}: {
  items: ReadonlyArray<NavItem>
  activeId: string
  signedIn: boolean
  onClose: () => void
  onNavigate: (path: string) => void
}) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())
  function toggleGroup(id: string) {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <div
      className="mobile-nav-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="mobile-nav-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Site navigation"
      >
        {items.map(l => l.kind === 'link' ? (
          <button
            key={l.id}
            type="button"
            className={`mobile-nav-link${activeId === l.id ? ' active' : ''}`}
            onClick={() => onNavigate(l.path)}
          >
            {l.label}
          </button>
        ) : (
          <Fragment key={l.id}>
            <button
              type="button"
              className={`mobile-nav-link mobile-nav-group${activeId === l.id ? ' active' : ''}`}
              aria-expanded={openGroups.has(l.id)}
              onClick={() => toggleGroup(l.id)}
            >
              <span>{l.label}</span>
              <span className="mobile-nav-chevron">▾</span>
            </button>
            {openGroups.has(l.id) && (
              <div className="mobile-nav-subgroup">
                {l.items.map(i => (
                  <button
                    key={i.id}
                    type="button"
                    className="mobile-nav-link mobile-nav-sublink"
                    onClick={() => onNavigate(i.path)}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            )}
          </Fragment>
        ))}
        {!signedIn && (
          <>
            <div className="mobile-nav-divider" />
            <button className="mobile-nav-link" onClick={() => onNavigate('/workspace')}>Sign in</button>
            <button className="mobile-nav-link primary" onClick={() => onNavigate('/get-started')}>
              npm i -g instadash
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function AccountMenu({ label, email, onApiKeys, onBilling, onSignOut }: {
  label: string
  email: string
  onApiKeys: () => void
  onBilling: () => void
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    // Use `click` (not `mousedown`) so the menu item's own onClick fires first
    // and gets to navigate/sign-out before we close ourselves. mousedown raced
    // with the item's click in some browsers.
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost btn-mono"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <span className="dot" style={{ color: '#B8FF3A' }}></span>
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>▾</span>
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          minWidth: 220,
          background: 'var(--bg-1)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          padding: 6,
          zIndex: 100,
        }}>
          <div className="mono" style={{ padding: '8px 12px 4px', fontSize: 11, color: 'var(--fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {email}
          </div>
          <div style={{ height: 1, background: 'var(--line-soft)', margin: '6px 0' }} />
          <MenuItem onClick={() => { onApiKeys(); setOpen(false) }}>API Keys</MenuItem>
          <MenuItem onClick={() => { onBilling(); setOpen(false) }}>Manage billing</MenuItem>
          <div style={{ height: 1, background: 'var(--line-soft)', margin: '6px 0' }} />
          <MenuItem onClick={() => { onSignOut(); setOpen(false) }} danger>Sign out</MenuItem>
        </div>
      )}
    </div>
  )
}

function MenuItem({ children, onClick, danger = false }: {
  children: ReactNode
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick() }}
      className="mono"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        background: 'transparent',
        border: 'none',
        color: danger ? 'var(--err, #ff6b6b)' : 'var(--fg)',
        cursor: 'pointer',
        borderRadius: 4,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-2)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      {children}
    </button>
  )
}

interface FooterLink { label: string; path: string; external?: boolean }

function SiteFooter() {
  const navigate = useNavigate()
  const cols: Array<{ title: string; items: FooterLink[] }> = [
    { title: 'Product', items: [
      { label: 'Features', path: '/' },
      { label: 'Showcase', path: '/showcase' },
      { label: 'Pricing',  path: '/pricing' },
    ]},
    { title: 'Build', items: [
      { label: 'Docs',             path: '/docs' },
      { label: 'Recipes',          path: '/recipes' },
      { label: 'MCP setup',        path: '/mcp' },
      { label: 'Agents manifesto', path: '/agents' },
    ]},
    { title: 'Resources', items: [
      { label: 'llms.txt',        path: '/llms.txt',                                external: true },
      { label: 'mcp.json',        path: '/.well-known/mcp.json',                    external: true },
      { label: 'Mesh Search API', path: '/docs/mesh-protocol' },
      { label: 'GitHub',          path: 'https://github.com/instadashio/instadash' },
      { label: 'Smithery',        path: 'https://smithery.ai/servers/instadash/instadash' },
    ]},
    { title: 'Company', items: [
      { label: 'Contact', path: '/contact' },
      { label: 'Privacy', path: '/privacy' },
      { label: 'Terms',   path: '/terms' },
    ]},
  ]
  function handleClick(item: FooterLink) {
    if (/^(mailto:|https?:)/.test(item.path)) {
      window.open(item.path, '_blank', 'noopener,noreferrer')
    } else if (item.external) {
      window.location.href = item.path
    } else {
      navigate(item.path)
    }
  }
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <Logo size={18} />
            <p className="lede" style={{ fontSize: 13, marginTop: 14, color: 'var(--fg-3)', maxWidth: 280 }}>
              The hosted data surface for AI agents. MCP-native, token-efficient, human-in-the-loop.
            </p>
            <div className="row gap-8" style={{ marginTop: 18 }}>
              <span className="chip mono" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                <span className="dot" style={{ color: '#B8FF3A' }}></span> Private by default · Public when you choose
              </span>
            </div>
          </div>
          {cols.map(c => (
            <div className="footer-col" key={c.title}>
              <div className="footer-col-title">{c.title}</div>
              {c.items.map(item => (
                <a key={item.label} onClick={() => handleClick(item)}>{item.label}</a>
              ))}
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <span>© 2026 Instadash · Melbourne AU</span>
          <span>grid · api · llm context</span>
        </div>
      </div>
    </footer>
  )
}
